import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { wolfswapFactionsApi } from '@/lib/api';
import {
    fetchCollateral,
    fetchMarketDetail,
    fetchMarkets,
    type CollateralResponse,
    type MarketDetailResponse,
    type SpeedMarket,
} from '@/lib/api/speed';
import { queryKeys } from '@/lib/query';

import type { Coin, NetworkId, PairSymbol } from '../types';
import { coinToPair, pairToCoin } from '../types';

const MARKETS_STALE_TIME_MS = 60 * 1000;
const MARKET_DETAIL_STALE_TIME_MS = 10 * 60 * 1000;
const MARKET_COLLATERAL_STALE_TIME_MS = 60 * 1000;

/**
 * Markets list, enriched with `symbols` (`<coin>-usd`) so the existing market
 * selector and price-stream consumers keep working unchanged.
 */
export interface MarketsResult {
    networkId: NetworkId;
    markets: SpeedMarket[];
    symbols: PairSymbol[];
}

async function fetchMarketsResult(): Promise<MarketsResult> {
    const data = await fetchMarkets(wolfswapFactionsApi);
    if (data.markets.length === 0) {
        throw new Error('No markets available');
    }
    return {
        networkId: data.networkId,
        markets: data.markets,
        symbols: data.markets.map((market) => coinToPair(market.coin)),
    };
}

export function tokenPairsQueryOptions(networkId: NetworkId) {
    return {
        queryKey: queryKeys.tokenPairs.byNetwork(networkId),
        queryFn: fetchMarketsResult,
        staleTime: MARKETS_STALE_TIME_MS,
    } satisfies UseQueryOptions<MarketsResult>;
}

export function tokenPairDetailQueryOptions(
    networkId: NetworkId,
    pair: PairSymbol,
) {
    const coin: Coin = pairToCoin(pair);
    return {
        queryKey: queryKeys.tokenPairs.detail(networkId, pair),
        queryFn: async () => fetchMarketDetail(wolfswapFactionsApi, coin),
        staleTime: MARKET_DETAIL_STALE_TIME_MS,
    } satisfies UseQueryOptions<MarketDetailResponse>;
}

export function tokenPairLeverageDetailQueryOptions(
    networkId: NetworkId,
    pair: PairSymbol,
    leverage: number,
) {
    const coin: Coin = pairToCoin(pair);
    return {
        queryKey: queryKeys.tokenPairs.leverageDetail(
            networkId,
            pair,
            leverage,
        ),
        queryFn: async () => fetchCollateral(wolfswapFactionsApi, coin, leverage),
        staleTime: MARKET_COLLATERAL_STALE_TIME_MS,
    } satisfies UseQueryOptions<CollateralResponse>;
}

/**
 * Hook to fetch the available speed-trading markets.
 *
 * @param networkId - Always the speed-trading network (999); kept for cache keying.
 */
export function useTokenPairs(
    networkId: NetworkId,
    options?: Omit<UseQueryOptions<MarketsResult>, 'queryKey' | 'queryFn'>,
) {
    return useQuery({
        ...tokenPairsQueryOptions(networkId),
        ...options,
    });
}

/**
 * Hook to fetch a market's leverage tiers / leverage info.
 *
 * @param pair - Market symbol, e.g. "btc-usd" (the coin is derived from it).
 */
export function useTokenPairDetail(
    networkId: NetworkId,
    pair: PairSymbol,
    options?: Omit<
        UseQueryOptions<MarketDetailResponse>,
        'queryKey' | 'queryFn'
    >,
) {
    return useQuery({
        ...tokenPairDetailQueryOptions(networkId, pair),
        ...options,
    });
}

/**
 * Hook to fetch the minimum USD collateral for a market at a given leverage.
 *
 * @param pair - Market symbol, e.g. "btc-usd".
 * @param leverage - Selected leverage.
 */
export function useTokenPairLeverageDetail(
    networkId: NetworkId,
    pair: PairSymbol,
    leverage: number,
    options?: Omit<UseQueryOptions<CollateralResponse>, 'queryKey' | 'queryFn'>,
) {
    return useQuery({
        ...tokenPairLeverageDetailQueryOptions(networkId, pair, leverage),
        ...options,
    });
}
